import { BellRing, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "./auth";
import { enablePushNotifications, pushStatus, reconcilePushNotifications } from "./push-notifications";

const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
const promptKey = (userId: string) => `ja:push-prompt:${userId}`;

function installedPwa() {
  return window.matchMedia("(display-mode: standalone)").matches
    || Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
}

export function PushNotificationPrompt() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user || !installedPwa() || !("Notification" in window) || Notification.permission !== "granted") return;
    let lastAttempt = 0;
    const reconcile = () => {
      const now = Date.now();
      if (now - lastAttempt < 60_000) return;
      lastAttempt = now;
      void reconcilePushNotifications()
        .then(() => setOpen(false))
        .catch(() => { /* Uma próxima retomada tentará reparar a inscrição novamente. */ });
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") reconcile();
    };
    reconcile();
    window.addEventListener("pageshow", reconcile);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pageshow", reconcile);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user]);

  useEffect(() => {
    if (!user || !installedPwa()) return;
    const snoozedUntil = Number(localStorage.getItem(promptKey(user.userId)));
    if (Number.isFinite(snoozedUntil) && snoozedUntil > Date.now()) return;
    let active = true;
    const timer = window.setTimeout(() => {
      if (document.querySelector(".passkey-prompt")) return;
      void pushStatus()
        .then((status) => { if (active && status === "prompt") setOpen(true); })
        .catch(() => { /* A central continua funcionando sem push. */ });
    }, 8_000);
    return () => { active = false; window.clearTimeout(timer); };
  }, [user]);

  if (!open || !user) return null;
  const dismiss = () => {
    localStorage.setItem(promptKey(user.userId), String(Date.now() + SNOOZE_MS));
    setOpen(false);
  };
  const activate = async () => {
    setBusy(true); setError("");
    try {
      await enablePushNotifications();
      localStorage.removeItem(promptKey(user.userId));
      setOpen(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Não foi possível ativar as notificações.");
    } finally { setBusy(false); }
  };

  return <aside className="push-optin" role="dialog" aria-labelledby="push-optin-title">
    <button type="button" className="icon-close" onClick={dismiss} aria-label="Agora não"><X size={17} /></button>
    <span className="push-optin-icon"><BellRing size={21} /></span>
    <span><strong id="push-optin-title">Ativar notificações</strong><small>Agenda, atrasos e alertas importantes.</small></span>
    {error && <p className="form-error" role="alert">{error}</p>}
    <button type="button" className="site-button site-button-red site-button-small" onClick={() => void activate()} disabled={busy}>
      {busy ? "Ativando…" : "Ativar"}
    </button>
  </aside>;
}
